/**
 * Server-state configuration (§32, §33).
 *
 * §32: "Invalidate/refetch authoritative server data after mutations rather
 * than duplicating it into many stores." So mastery, XP, recommendations and
 * profile live here and nowhere else — there is no parallel copy in a Zustand
 * store to drift out of date.
 */

import { QueryClient } from '@tanstack/react-query';

import { isApiError } from '@/api/errors';

export const queryKeys = {
  /** The family, for the picker. Cleared on every identity swap (§42). */
  profiles: ['profiles'] as const,
  profile: ['profile'] as const,
  mastery: ['mastery'] as const,
  skill: (id: string) => ['skill', id] as const,
  gamification: ['gamification'] as const,
  /** Whether the product is about to stop working. Read on every screen the
   *  banner can appear on, so it is one key, not one per page. */
  subscription: ['subscription'] as const,
  plans: ['plans'] as const,
  recommendations: ['recommendations'] as const,
  activity: ['activity'] as const,
  curriculum: ['curriculum'] as const,
  curriculumSkill: (id: string) => ['curriculum', 'skill', id] as const,
  skillChat: (id: string) => ['learn', 'chat', id] as const,
  submission: (id: string) => ['submission', id] as const,
  submissionResponse: (id: string) => ['submission', id, 'response'] as const,
  practiceSession: (id: string) => ['practice', id] as const,
  parentOverview: ['parent', 'overview'] as const,
  parentChildren: ['parent', 'children'] as const,
  parentProgress: (id: string) => ['parent', 'progress', id] as const,
  childSignInLink: (id: string) => ['parent', 'sign-in-link', id] as const,
};

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Learning state changes when the child acts, not on a timer. Refetch
        // on focus so a child returning to the tab sees current XP, but do not
        // poll — §52 says not to block Home on non-critical work, and a child
        // on mobile data pays for every poll.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry(failureCount, error) {
          // §16: only retry what the backend marked retryable. Retrying a 403
          // or a validation failure wastes the child's connection and can look
          // like the app is stuck.
          if (isApiError(error)) {
            if (error.isAuthFailure) return false;
            return error.canRetry && failureCount < 2;
          }
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // Mutations are never retried automatically. §55 requires idempotency
        // keys precisely because a retry that the user did not ask for can
        // duplicate an attempt; the UI offers an explicit "try again" instead.
        retry: false,
      },
    },
  });
}
